import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../widgets/IconButton';
import { ChevronsLeft, ChevronsRight, CircleUserRound, Menu, X } from 'lucide-react';
import { useBrowserDetection } from '../../utils/browserUtils';
import { getNavigationCategories } from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { checkAuthStatus } from '../../utils/authUtils';
import { useUserLevel } from '../../hooks/useUserLevel';
import { normalizeLanguage } from '../../i18n/language';

const SIDEBAR_HEADER_HEIGHT_PX = 60;
const SIDEBAR_HEADER_HEIGHT_CLASS = 'h-[60px]';
const SIDEBAR_COLLAPSED_WIDTH_CLASS = 'w-14'; // 56px
const SIDEBAR_EXPANDED_WIDTH_CLASS = 'w-60'; // 240px
const SIDEBAR_LOGO_HEIGHT = 'h-5';
const SIDEBAR_ICON_SIZE = 'w-4 h-4';
const SIDEBAR_NAV_ICON_CONTAINER_CLASS = 'w-10 h-10';
const SIDEBAR_AVATAR_SIZE = 'w-8 h-8';
const SIDEBAR_AVATAR_CONTAINER_CLASS = 'w-10 h-10';

interface AppSidebarProps {
    isMenuOpen: boolean;
    isCollapsed: boolean;
    onCollapseToggle: (collapsed: boolean) => void;
    onItemClick?: () => void;
}

const avatarImages = ['avatar_01.png'];

export const AppSidebar: React.FC<AppSidebarProps> = ({ isMenuOpen, isCollapsed, onCollapseToggle, onItemClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();
    const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    const isEn = language === 'en';
    const tx = (zh: string, en: string) => (isEn ? en : zh);
    const { safeCSS, browser } = useBrowserDetection();
    const { openAuthModal } = useAuth();
    const { userLevel } = useUserLevel();

    // -- State --
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [avatar, setAvatar] = useState('');
    const [userInfo, setUserInfo] = useState({ display_name: tx('未登录', 'Guest') });

    // -- Effects --
    useEffect(() => {
        const checkAuth = () => {
            const role = localStorage.getItem('userRole');
            const isAuth = localStorage.getItem('isAuthenticated') === 'true';
            setIsAuthenticated(isAuth);
            setIsAdmin(isAuth && role === 'ADMIN');
            // Update userInfo whenever auth state changes
            setUserInfo(getUserInfo());
        };

        checkAuth();
        window.addEventListener('authStateChange', checkAuth);
        window.addEventListener('userProfileUpdate', checkAuth);

        setAvatar(avatarImages[Math.floor(Math.random() * avatarImages.length)]);

        return () => {
            window.removeEventListener('authStateChange', checkAuth);
            window.removeEventListener('userProfileUpdate', checkAuth);
        };
    }, []);

    // -- Helpers --
    const getUserInfo = () => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return { display_name: tx('未登录', 'Guest') };
            const user = JSON.parse(userStr);
            return {
                display_name: user.user_name || user.name || user.phone || 'User',
                ...user
            };
        } catch {
            return { display_name: 'User' };
        }
    };

    const getLevelName = (level: string) => {
        switch (level) {
            case 'LEVEL_1': return tx('新手卖家', 'Starter seller');
            case 'LEVEL_2': return tx('专业卖家', 'Professional seller');
            default: return tx('免费用户', 'Free user');
        }
    };
    const navigationCategories = getNavigationCategories(language);

    // userInfo is now a state variable updated in checkAuth, no longer a memo

    // -- Sub-Components (Internal) --

    // Navigation Item Button Component
    const NavItemButton: React.FC<{
        label: string;
        path?: string;
        disabled?: boolean;
        isActive?: boolean;
        icon?: React.ReactNode;
        isExternal?: boolean;
        isCollapsed: boolean;
        navigate: (path: string) => void;
        onClick?: () => void;
    }> = ({ label, path, disabled, isActive, icon, isExternal = false, isCollapsed, navigate, onClick }) => (
        <button
            onClick={() => {
                if (disabled || !path) return;

                if (isExternal) {
                    window.open(path, '_blank');
                } else {
                    navigate(path);
                }
                onClick?.();
            }}
            disabled={disabled}
            title={isCollapsed ? label : undefined}
            className={`w-full flex items-center h-10 rounded-xl transition-all duration-300 overflow-hidden group
    ${isActive
                    ? 'bg-b text-w font-semibold'
                    : !disabled
                        ? 'text-b6 md:hover:bg-b05 active:bg-b active:text-w'
                        : 'text-b6'}
    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
  `}
        >
            {/* Icon Container */}
            <div
                className={`${isCollapsed ? 'w-10 h-10' : SIDEBAR_NAV_ICON_CONTAINER_CLASS} flex items-center justify-center flex-shrink-0 transition-all duration-300
      ${isCollapsed ? 'rounded-xl' : ''}
      ${isActive
                        ? 'text-w'
                        : !disabled
                            ? 'group-hover:text-b group-active:text-w'
                            : ''}
      ${isCollapsed && isActive ? 'bg-b' : ''}
      ${isCollapsed && !isActive && !disabled ? 'group-active:bg-b' : ''}
    `}
            >
                {icon && React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, {
                    className: `${SIDEBAR_ICON_SIZE} transition-colors duration-300`
                }) : icon}
            </div>
            {/* Label Area (Expanded only) */}
            <div
                className={`flex items-center h-full overflow-hidden transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100'}
    `}
            >
                <span className={`truncate block text-sm transition-colors duration-300 whitespace-nowrap pr-4 ${isActive ? 'text-w font-bold' : 'md:group-hover:text-b'}`}>{label}</span>
            </div>
        </button>
    );

    return (
        <aside
            className={`fixed left-0 bg-w overflow-hidden z-[100] transition-all duration-300 flex flex-col border-r-[1px] border-b05
        ${isMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'} 
        md:relative md:left-auto md:translate-x-0 md:shadow-none
        ${isCollapsed ? SIDEBAR_COLLAPSED_WIDTH_CLASS : SIDEBAR_EXPANDED_WIDTH_CLASS}
      `}
            style={{
                top: !browser.isDesktop ? 0 : undefined,
                height: '100%',
                flexShrink: 0,
            }}
        >
            {/* 1. Header with Logo and Toggle */}
            <div className={`flex items-center justify-between ${isCollapsed ? 'px-2' : 'pl-5 pr-2'} flex-shrink-0 ${SIDEBAR_HEADER_HEIGHT_CLASS}`}
                style={{
                    paddingTop: !browser.isDesktop ? safeCSS.topSafeArea : undefined,
                    height: !browser.isDesktop ? `calc(${safeCSS.topSafeArea} + ${SIDEBAR_HEADER_HEIGHT_PX}px)` : SIDEBAR_HEADER_HEIGHT_PX
                }}
            >
                {/* Desktop Toggle or Mobile Brand */}
                {isCollapsed ? (
                    <div className="w-full flex justify-center">
                            <IconButton
                                icon={<ChevronsRight />}
                                onClick={() => onCollapseToggle(false)}
                                title={tx('展开菜单', 'Expand menu')}
                                size="md"
                                variant="default"
                                shape="square"
                        />
                    </div>
                ) : (
                    <>
                        <div className="cursor-pointer" onClick={() => {
                            navigate('/');
                            onItemClick?.();
                        }}>
                            <img
                                src="/images/Logo_Color.svg"
                                alt="SSEEAA"
                                className={`${SIDEBAR_LOGO_HEIGHT} w-auto`}
                            />
                        </div>
                        {/* Desktop Only Toggle */}
                        <div className="hidden md:block">
                            <IconButton
                                icon={<ChevronsLeft />}
                                onClick={() => onCollapseToggle(true)}
                                title={tx('收起菜单', 'Collapse menu')}
                                size="md"
                                variant="default"
                                shape="square"
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
                {/* 2. Scrollable Navigation Area */}
                <nav className={`flex-1 overflow-y-auto py-2 ${isCollapsed ? 'px-2' : 'px-2'}`}>
                    <div>
                        {navigationCategories.map((category, idx) => {
                            const visibleItems = category.items.filter(item => !item.adminOnly || isAdmin);
                            if (visibleItems.length === 0) return null;

                            return (
                                <div key={idx} className="flex flex-col gap-2">
                                    {idx > 0 && <div className="h-6" />}
                                    {visibleItems.map((item, itemIdx) => (
                                        <NavItemButton
                                            key={itemIdx}
                                            label={item.label}
                                            path={item.path}
                                            disabled={item.disabled}
                                            isActive={item.activeMatch ? item.activeMatch(location.pathname) : location.pathname.startsWith(item.path || '#')}
                                            icon={item.icon}
                                            isExternal={item.isExternal}
                                            isCollapsed={isCollapsed}
                                            navigate={navigate}
                                            onClick={onItemClick}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </nav>

                {/* 3. User Menu / Login Area */}
                <div className="flex-shrink-0">
                    {!isAuthenticated ? (
                        isCollapsed ? (
                            <div className="px-2 py-2">
                                <button
                                    onClick={() => openAuthModal(location.pathname, 'login', false, 'manual')}
                                    title={tx('登录或注册', 'Login or sign up')}
                                    className="w-full p-3 bg-sseeaa text-w rounded-xl hover:shadow-md active:bg-b active:text-w transition-all duration-300 flex items-center justify-center"
                                >
                                    <CircleUserRound className="w-5 h-5 transition-colors duration-300" />
                                </button>
                            </div>
                        ) : (
                            <div className="px-5 py-4">
                                <button
                                    onClick={() => {
                                        openAuthModal(location.pathname, 'login', false, 'manual');
                                        onItemClick?.();
                                    }}
                                    className="w-full px-4 py-2 text-sm font-semibold bg-sseeaa text-w rounded-xl hover:shadow-md transition-all duration-200"
                                >
                                    {tx('登录或注册', 'Login or sign up')}
                                </button>
                            </div>
                        )
                    ) : (
                        <div className={`transition-all duration-300 ${isCollapsed ? 'px-2 py-2' : 'px-2 py-2'}`}>
                            <div
                                className={`flex items-center rounded-xl cursor-pointer transition-all duration-200 overflow-hidden group
                  ${isCollapsed ? '' : 'md:hover:bg-b05 active:bg-b active:text-w'}
                `}
                                onClick={() => {
                                    // Double-check authentication before navigating to profile
                                    if (isAuthenticated && localStorage.getItem('authToken')) {
                                        navigate('/user/profile');
                                        onItemClick?.();
                                    } else {
                                        // If somehow auth is stale, open login modal
                                        openAuthModal(location.pathname, 'login', false, 'manual');
                                        onItemClick?.();
                                    }
                                }}
                                title={isCollapsed ? userInfo.display_name : undefined}
                            >
                                <div
                                    className={`${SIDEBAR_AVATAR_CONTAINER_CLASS} flex items-center justify-center flex-shrink-0 transition-all duration-300
                    ${isCollapsed ? 'rounded-xl' : ''}
                     ${isCollapsed ? 'md:hover:bg-b05 active:bg-b active:text-w' : ''}
                  `}
                                >
                                    <img
                                        src={`/images/avatar/${avatar}`}
                                        alt="Avatar"
                                        className={`${SIDEBAR_AVATAR_SIZE} rounded-full object-cover`}
                                    />
                                </div>

                                <div
                                    className={`flex flex-col justify-center overflow-hidden transition-all duration-300 ease-in-out
                    ${isCollapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100 pl-2 pr-4'}
                  `}
                                >
                                    <div className="text-xs font-bold truncate text-b group-active:text-w transition-colors whitespace-nowrap">
                                        {userInfo.display_name}
                                    </div>
                                    <div className="text-[10px] text-b4 truncate group-active:text-w transition-colors whitespace-nowrap">
                                        {getLevelName(userLevel)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

interface MobileHeaderProps {
    isMenuOpen: boolean;
    onToggleMenu: () => void;
    isScrolled?: boolean;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({ isMenuOpen, onToggleMenu, isScrolled }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    const tx = (zh: string, en: string) => (language === 'en' ? en : zh);
    const { safeCSS } = useBrowserDetection();

    return (
        <div
            className={`md:hidden fixed inset-x-0 top-0 flex items-center justify-between bg-bgb px-5 z-40 transition-all duration-300 border-b-[1px] ${SIDEBAR_HEADER_HEIGHT_CLASS} ${isScrolled ? 'border-b05' : 'border-transparent'}`}
            style={{
                paddingTop: safeCSS.topSafeArea,
                height: `calc(${safeCSS.topSafeArea} + ${SIDEBAR_HEADER_HEIGHT_PX}px)`
            }}
        >
            <img
                src="/images/Logo_Color.svg"
                alt="SSEEAA"
                className="h-5 cursor-pointer"
                onClick={() => navigate('/')}
            />
            <IconButton
                icon={isMenuOpen ? <ChevronsLeft /> : <Menu />}
                onClick={onToggleMenu}
                size="md"
                variant="default"
                shape="square"
                title={isMenuOpen ? tx('关闭菜单', 'Close menu') : tx('打开菜单', 'Open menu')}
            />
        </div>
    );
};
